import {
  ActionBarPrimitive,
  AuiIf,
  ChainOfThoughtByIndicesProvider,
  MessagePrimitive,
  groupPartByType,
  type EnrichedPartState,
  useAuiState,
} from "@assistant-ui/react";
import { Copy, Sparkles } from "lucide-react";
import { MarkdownText } from "../assistant-ui/markdown-text";
import type { MCAPSuspensionPayload } from "../../hooks/useCopilotRuntime";
import { attachmentToChip } from "./contextChips";
import { ChainOfThoughtSection } from "./ChainOfThoughtSection";
import { ContextChipBadge } from "./ContextChipBadge";
import { McapApprovalCard } from "./McapApprovalCard";
import { ReloadResponseButton } from "./ReloadResponseButton";
import type { CopilotCreditUsage } from "../../hooks/useCopilotRuntime";

// Groups adjacent reasoning parts into a single collapsible "Thinking" section.
const groupReasoningParts = groupPartByType({
  reasoning: ["group-chainOfThought"],
});

function RunCreditUsage() {
  const isRunning = useAuiState((s) => s.message.status?.type === "running");
  const usage = useAuiState(
    (s) =>
      s.message.metadata.custom?.creditUsage as CopilotCreditUsage | undefined,
  );
  if (isRunning || !usage?.creditsConsumed) return null;

  const formatter = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
  });
  return (
    <span className="py-1 text-[11px] tabular-nums text-neutral-500">
      {formatter.format(usage.creditsConsumed)} credits
    </span>
  );
}

/** Renders one thread message (user bubble or assistant turn). */
export function CopilotMessage() {
  return (
    <MessagePrimitive.Root className="group">
      <AuiIf condition={(s) => s.message.role === "user"}>
        <div className="mb-3 flex flex-col items-end gap-1.5">
          <div className="flex flex-wrap justify-end gap-1.5">
            <MessagePrimitive.Attachments>
              {({ attachment }) => (
                <ContextChipBadge
                  key={attachment.id}
                  chip={attachmentToChip(attachment)}
                />
              )}
            </MessagePrimitive.Attachments>
          </div>
          <div className="max-w-[88%] rounded-2xl rounded-tr-sm border border-neutral-500/50 bg-neutral-600/30 px-3.5 py-2.5 shadow-sm shadow-neutral-900/20">
            <MessagePrimitive.Parts>
              {({ part }: { part: EnrichedPartState }) => {
                if (part.type === "text") {
                  return (
                    <div className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-50">
                      <MarkdownText />
                    </div>
                  );
                }
                return null;
              }}
            </MessagePrimitive.Parts>
          </div>
        </div>
      </AuiIf>

      <AuiIf condition={(s) => s.message.role === "assistant"}>
        <div className="mb-5">
          <div className="mb-2 flex items-center gap-1.5">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#FD3E5E] to-[#FF8F15]">
              <Sparkles className="h-3 w-3 text-white" />
            </div>
            <span className="text-[11px] text-neutral-300 font-semibold uppercase tracking-wider">
              Copilot
            </span>
          </div>

          <div className="text-sm leading-relaxed text-neutral-200">
            <MessagePrimitive.GroupedParts groupBy={groupReasoningParts}>
              {({ part }) => {
                switch (part.type) {
                  case "group-chainOfThought": {
                    const [startIndex, endIndex] = [
                      part.indices[0],
                      part.indices[part.indices.length - 1],
                    ];
                    return (
                      <ChainOfThoughtByIndicesProvider
                        startIndex={startIndex}
                        endIndex={endIndex}
                      >
                        <ChainOfThoughtSection />
                      </ChainOfThoughtByIndicesProvider>
                    );
                  }
                  case "text":
                    return <MarkdownText />;
                  case "reasoning":
                    return (
                      <div className="rounded-md border border-neutral-700/40 bg-neutral-950/80 p-3 font-mono text-[11px] leading-relaxed text-neutral-500 whitespace-pre-wrap">
                        {part.text}
                      </div>
                    );
                  case "data":
                    if (part.name === "mcap_suspension") {
                      const suspension = part.data as MCAPSuspensionPayload;
                      return (
                        // Key by thread+incident so switching conversations
                        // remounts a fresh card. Without it React reuses the
                        // component instance (same type, same tree position)
                        // and the previous thread's approval/live state leaks
                        // into the newly opened conversation.
                        <McapApprovalCard
                          key={`${suspension.thread_id ?? "nothread"}:${suspension.incident_id}`}
                          payload={suspension}
                        />
                      );
                    }
                    return null;
                  default:
                    return null;
                }
              }}
            </MessagePrimitive.GroupedParts>
          </div>

          <div className="mt-2 flex min-h-7 items-center justify-between gap-3">
            <ActionBarPrimitive.Root
              hideWhenRunning
              autohide="never"
              className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100"
            >
              <ActionBarPrimitive.Copy className="flex items-center gap-1 rounded-md px-2 py-1 text-neutral-400 transition-colors hover:text-neutral-300 data-[copied]:text-emerald-400">
                <Copy className="h-4 w-4" />
              </ActionBarPrimitive.Copy>
              <ReloadResponseButton />
            </ActionBarPrimitive.Root>
            <RunCreditUsage />
          </div>
        </div>
      </AuiIf>
    </MessagePrimitive.Root>
  );
}
